// What a workspace calls its reports. One company writes Stundenlohnberichte, the next Tagelohnscheine,
// an Austrian one Regiescheine and a Swiss one Regierapporte — the same record, named by region, by
// contract law (VOB/B § 15 says "Stundenlohnzettel", SIA 118 "Regierapport") or by habit. A crew that
// reads a word it does not use has to translate every screen, so the name is a setting (Einstellungen →
// Bezeichnungen) and every text that names the record is written against it.
//
// How a text names a record: the locale bundles carry tokens instead of the word, and
// `applyTerminology` in plugins/i18n resolves them once per bundle whenever the settings arrive. The
// syntax, and why grammar is part of it, is in docs/terminology.md. In short:
//
//   [[hwr]]        singular              Stundenlohnbericht    hourly wage report
//   [[hwr.pl]]     plural                Stundenlohnberichte   hourly wage reports
//   [[hwr.gen]]    genitive singular     Stundenlohnberichts   (German only)
//   [[hwr.datpl]]  dative plural         Stundenlohnberichten  (German only)
//   [[Hwr]]        first letter upper                          Hourly wage report
//   [[HWR]]        title case                                  Hourly Wage Report
//   [[hwr:der/die/das]]  the word matching the term's gender (German: m/f/n)
//   [[hwr:a/an]]         the article matching the term's sound (English)
//
// The backend keeps only the overrides (`settings.terminology`); a term nobody changed is resolved from
// the defaults here, so a new workspace and one that predates the setting read exactly the same.

export const GENDER = Object.freeze({ MASCULINE: "MASCULINE", FEMININE: "FEMININE", NEUTER: "NEUTER" })

const GENDER_INDEX = { [GENDER.MASCULINE]: 0, [GENDER.FEMININE]: 1, [GENDER.NEUTER]: 2 }

/**
 * The records a workspace may rename: the key the backend stores an override under, and the short name
 * the message tokens use. The order is the order of the settings table.
 */
export const TERMS = Object.freeze([
  { key: "HOURLY_WAGE_REPORT", token: "hwr" },
  { key: "CONSTRUCTION_REPORT", token: "cr" },
  { key: "HOURLY_WAGE_INVOICE", token: "hwi" },
])

const TERM_BY_TOKEN = Object.fromEntries(TERMS.map(term => [term.token, term]))

/**
 * The names in use, per language and record; the first of each list is the default.
 *
 * `region` (ISO 3166) and `norm` are shown next to a preset in the picker so a reader recognises the
 * word of their own market. Both are only set where the word is the one that market's standard
 * contract text uses (VOB/B § 15, SIA 118 Art. 46) or where it is clearly regional — a word used
 * everywhere carries neither.
 *
 * `article` is the English indefinite article ("a"/"an"); it cannot be told from the spelling alone
 * ("an hourly …", "a T&M …"), so every English preset states it.
 */
export const PRESETS = Object.freeze({
  de: {
    HOURLY_WAGE_REPORT: [
      { singular: "Stundenlohnbericht", plural: "Stundenlohnberichte", gender: GENDER.MASCULINE },
      { singular: "Stundenlohnzettel", plural: "Stundenlohnzettel", gender: GENDER.MASCULINE, norm: "VOB/B § 15" },
      { singular: "Stundenlohnarbeit", plural: "Stundenlohnarbeiten", gender: GENDER.FEMININE },
      { singular: "Tagelohnschein", plural: "Tagelohnscheine", gender: GENDER.MASCULINE },
      { singular: "Tagelohnzettel", plural: "Tagelohnzettel", gender: GENDER.MASCULINE },
      { singular: "Regiebericht", plural: "Regieberichte", gender: GENDER.MASCULINE },
      { singular: "Regieschein", plural: "Regiescheine", gender: GENDER.MASCULINE, region: "AT" },
      { singular: "Regiezettel", plural: "Regiezettel", gender: GENDER.MASCULINE },
      { singular: "Regierapport", plural: "Regierapporte", gender: GENDER.MASCULINE, region: "CH", norm: "SIA 118" },
    ],
    CONSTRUCTION_REPORT: [
      { singular: "Bautagesbericht", plural: "Bautagesberichte", gender: GENDER.MASCULINE },
      { singular: "Bautagebuch", plural: "Bautagebücher", gender: GENDER.NEUTER },
      { singular: "Tagesbericht", plural: "Tagesberichte", gender: GENDER.MASCULINE },
      { singular: "Baustellenbericht", plural: "Baustellenberichte", gender: GENDER.MASCULINE },
      { singular: "Tagesrapport", plural: "Tagesrapporte", gender: GENDER.MASCULINE, region: "CH" },
    ],
    HOURLY_WAGE_INVOICE: [
      { singular: "Stundenlohnabrechnung", plural: "Stundenlohnabrechnungen", gender: GENDER.FEMININE },
      { singular: "Stundenlohnrechnung", plural: "Stundenlohnrechnungen", gender: GENDER.FEMININE },
      { singular: "Tagelohnabrechnung", plural: "Tagelohnabrechnungen", gender: GENDER.FEMININE },
      { singular: "Tagelohnrechnung", plural: "Tagelohnrechnungen", gender: GENDER.FEMININE },
      { singular: "Regieabrechnung", plural: "Regieabrechnungen", gender: GENDER.FEMININE },
      { singular: "Regierechnung", plural: "Regierechnungen", gender: GENDER.FEMININE },
    ],
  },
  en: {
    HOURLY_WAGE_REPORT: [
      { singular: "hourly wage report", plural: "hourly wage reports", article: "an" },
      { singular: "daywork sheet", plural: "daywork sheets", article: "a", region: "GB" },
      { singular: "daywork ticket", plural: "daywork tickets", article: "a" },
      { singular: "time and materials report", plural: "time and materials reports", article: "a", region: "US" },
      { singular: "T&M ticket", plural: "T&M tickets", article: "a", region: "US" },
    ],
    CONSTRUCTION_REPORT: [
      { singular: "construction report", plural: "construction reports", article: "a" },
      { singular: "daily report", plural: "daily reports", article: "a" },
      { singular: "site diary", plural: "site diaries", article: "a", region: "GB" },
      { singular: "daily log", plural: "daily logs", article: "a", region: "US" },
      { singular: "site report", plural: "site reports", article: "a" },
    ],
    HOURLY_WAGE_INVOICE: [
      { singular: "hourly wage invoice", plural: "hourly wage invoices", article: "an" },
      { singular: "daywork invoice", plural: "daywork invoices", article: "a", region: "GB" },
      { singular: "time and materials invoice", plural: "time and materials invoices", article: "a", region: "US" },
      { singular: "T&M invoice", plural: "T&M invoices", article: "a", region: "US" },
    ],
  },
})

/** Languages whose texts are written against terms — every bundle that carries tokens. */
export const TERM_LANGUAGES = Object.freeze(Object.keys(PRESETS))

/** Longest name the backend accepts (FrontendTermPayload, kept in step with it). */
export const TERM_MAX_LENGTH = 40

// What a name may consist of, per language, kept in step with the backend. Never the characters
// vue-i18n's message syntax reserves (`{ } @ |`) or markup: a name lands inside message sources.
// German names are one word — a compound or hyphenated (Regieschein, Regie-Nachweis): the endings a
// text needs (des Regiescheins, den Regiescheinen) go on the end of the name, and in a name of several
// words that is not the word that declines ("aus drei Regieberichte Tiefbaun").
const NAME_PATTERNS = {
  de: /^[\p{L}\p{N}]+(-[\p{L}\p{N}]+)*$/u,
  en: /^[\p{L}\p{N}][\p{L}\p{N} .&/'’-]*$/u,
}

/** Whether `value` is a name the backend stores for `language` (surrounding blanks aside). */
export function isValidName(language, value) {
  const name = value?.trim() ?? ""
  return name.length > 0 && name.length <= TERM_MAX_LENGTH && (NAME_PATTERNS[language] ?? NAME_PATTERNS.en).test(name)
}

/** The overrides a save would be refused for — a blank, too long or ill-formed singular or plural. */
export function invalidTerms(overrides) {
  return (overrides ?? []).filter(
    term => !isValidName(term.language, term.singular) || !isValidName(term.language, term.plural)
  )
}

export function defaultTerm(language, key) {
  return PRESETS[language]?.[key]?.[0]
}

/** The preset a stored term is, if it is one — matched on the singular, which is what the picker shows. */
export function findPreset(language, key, singular) {
  return PRESETS[language]?.[key]?.find(preset => preset.singular === singular?.trim())
}

// ── Suggestions for a name that is not a preset ──────────────────────────────────────────────────────
// German compounds take gender and plural from their last part, and the parts a report name ends in are
// few. Covering those means a typed "Leistungsnachweis" arrives with "Leistungsnachweise" and "der"
// already filled in; anything unknown keeps the record's default gender and asks for the plural.
const GERMAN_HEADS = [
  ["bericht", "berichte", GENDER.MASCULINE],
  ["schein", "scheine", GENDER.MASCULINE],
  ["zettel", "zettel", GENDER.MASCULINE],
  ["rapport", "rapporte", GENDER.MASCULINE],
  ["nachweis", "nachweise", GENDER.MASCULINE],
  ["beleg", "belege", GENDER.MASCULINE],
  ["eintrag", "einträge", GENDER.MASCULINE],
  ["auftrag", "aufträge", GENDER.MASCULINE],
  ["protokoll", "protokolle", GENDER.NEUTER],
  ["journal", "journale", GENDER.NEUTER],
  ["buch", "bücher", GENDER.NEUTER],
  ["blatt", "blätter", GENDER.NEUTER],
  ["arbeit", "arbeiten", GENDER.FEMININE],
  ["liste", "listen", GENDER.FEMININE],
  ["karte", "karten", GENDER.FEMININE],
  ["ung", "ungen", GENDER.FEMININE],
]

/**
 * Plural, gender and (English) article for a name typed into the picker, as far as they can be told
 * from the word. The settings tab pre-fills its fields with this; the user has the last word.
 */
export function suggestForms(language, key, singular) {
  const word = singular.trim()
  const preset = findPreset(language, key, word)
  if (preset) return { ...preset }
  if (language === "de") {
    const lower = word.toLowerCase()
    const head = GERMAN_HEADS.find(([ending]) => lower.endsWith(ending))
    if (head) {
      const [ending, pluralEnding, gender] = head
      const stem = word.slice(0, word.length - ending.length)
      // The part keeps its own case: "Regie-Nachweis" becomes "Regie-Nachweise", not "Regie-nachweise".
      const startsUpper = /\p{Lu}/u.test(word.charAt(stem.length))
      const plural = stem + (startsUpper ? pluralEnding.charAt(0).toUpperCase() + pluralEnding.slice(1) : pluralEnding)
      return { singular: word, plural, gender }
    }
    return { singular: word, plural: word, gender: defaultTerm(language, key).gender }
  }
  return { singular: word, plural: englishPlural(word), article: englishArticle(word) }
}

function englishPlural(word) {
  if (/[^aeiou]y$/i.test(word)) return word.slice(0, -1) + "ies"
  if (/(s|x|z|ch|sh)$/i.test(word)) return word + "es"
  return word + "s"
}

// Good enough for a pre-fill: vowels take "an", so does a silent h, and so does an initialism whose
// first letter is spoken with a vowel ("an M&T …", "an RFI …").
function englishArticle(word) {
  if (/^(hour|honest|honou?r|heir)/i.test(word)) return "an"
  if (/^(uni|use|usu|eu|one\b)/i.test(word)) return "a"
  if (/^[A-Z]{1,4}(\b|&|-)/.test(word)) return /^[AEFHILMNORSX]/.test(word) ? "an" : "a"
  return /^[aeiou]/i.test(word) ? "an" : "a"
}

// ── Resolving ────────────────────────────────────────────────────────────────────────────────────────

/**
 * Every term of one language with all the forms a token can ask for: the workspace's override where
 * there is one, the default otherwise.
 *
 * @param {string} language
 * @param {Array<{term: string, language: string, singular: string, plural: string, gender?: string}>} overrides
 */
export function termsFor(language, overrides = []) {
  const words = {}
  for (const { key, token } of TERMS) {
    const fallback = defaultTerm(language, key)
    if (!fallback) continue
    const stored = overrides?.find(override => override.term === key && override.language === language)
    const term = stored
      ? { ...fallback, ...stored, article: stored.article ?? suggestForms(language, key, stored.singular).article }
      : fallback
    words[token] = language === "de" ? germanForms(term) : englishForms(term)
  }
  return words
}

function germanForms({ singular, plural, gender }) {
  // Genitive: feminine nouns do not change; masculine and neuter ones take -s, or -es after a sibilant
  // (des Nachweises). Dative plural adds -n unless the plural already ends in -n or -s (den Zetteln,
  // den Abrechnungen, den Büchern).
  const genitive =
    gender === GENDER.FEMININE ? singular : /(s|ß|x|z)$/.test(singular) ? `${singular}es` : `${singular}s`
  const dativePlural = /[ns]$/.test(plural) ? plural : `${plural}n`
  return { sg: singular, pl: plural, gen: genitive, datpl: dativePlural, variant: GENDER_INDEX[gender] ?? 0 }
}

function englishForms({ singular, plural, article }) {
  return { sg: singular, pl: plural, variant: article === "an" ? 1 : 0 }
}

// `[[hwr]]`, `[[Hwr.pl]]`, `[[hwr:der/die/das]]`. The key's case says how to capitalise the result.
const TOKEN = /\[\[([A-Za-z]+)(?:\.([a-z]+))?(?::([^\]]*))?\]\]/g

// The characters vue-i18n's message syntax reserves. A name cannot contain them (the backend refuses
// them), but a term lands inside a message source, so it is escaped rather than trusted.
const RESERVED = /[{}@|]/g

const TITLE_CASE_KEEP = new Set(["a", "an", "and", "of", "the", "for", "to", "in", "on", "or"])

function capitalise(text) {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

function titleCase(text) {
  return text
    .split(" ")
    .map((word, index) => (index > 0 && TITLE_CASE_KEEP.has(word) ? word : capitalise(word)))
    .join(" ")
}

/**
 * `text` with every term token replaced by the word it names in `words` (see `termsFor`). A token
 * this cannot resolve is left standing, visibly: that is a typo in a bundle, and a raw `[[…]]` on
 * screen gets fixed where a silently empty word would not.
 *
 * @param {boolean} [escape] whether the inserted words go into a vue-i18n message source
 */
export function resolveTerms(text, words, escape = false) {
  if (typeof text !== "string" || !text.includes("[[")) return text
  return text.replace(TOKEN, (token, key, form, choices) => {
    const forms = words[key.toLowerCase()]
    if (!forms || !TERM_BY_TOKEN[key.toLowerCase()]) return token
    if (choices !== undefined) {
      // Author-written words: no escaping, and no case change — the author spelled them as needed.
      const options = choices.split("/")
      return options[forms.variant] ?? options[0]
    }
    let word = forms[form ?? "sg"]
    if (word === undefined) return token
    if (key === key.toUpperCase() && key.length > 1) word = titleCase(word)
    else if (key[0] === key[0].toUpperCase()) word = capitalise(word)
    return escape ? word.replace(RESERVED, char => `{'${char}'}`) : word
  })
}

/**
 * A deep copy of a locale bundle with every token resolved. Message functions and other non-string
 * leaves are kept as they are.
 */
export function applyToBundle(bundle, words) {
  if (typeof bundle === "string") return resolveTerms(bundle, words, true)
  if (Array.isArray(bundle)) return bundle.map(entry => applyToBundle(entry, words))
  if (bundle && typeof bundle === "object") {
    const copy = {}
    for (const [key, value] of Object.entries(bundle)) copy[key] = applyToBundle(value, words)
    return copy
  }
  return bundle
}
